import AddIcCallIcon from '@mui/icons-material/AddIcCall';
import FilterListIcon from '@mui/icons-material/FilterList';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Button, Tab, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { Formik, FormikProps } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { useLocation } from 'react-router-dom';
import { useRecoilStateLoadable } from 'recoil';
import * as Yup from 'yup';
import Axios from '../../../common/axiosInterceptor';
import {
  trackModalUpdates,
  trackSaveInboundRouting,
} from '../../../common/userTracking';
import { isUrl } from '../../../common/validators';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import { methodNameEnum } from '../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import TabLabel from '../general/TabLabel';
import DestinationTabActions from '../InboundCallRules/DestinationTabActions';
import NumberTabActions from '../InboundCallRules/NumberTabActions';
import RuleTabActions from '../InboundCallRules/RuleTabActions';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';
import { getErrorTabIndex, isErrorInTab } from './DevicesAndRoutingModal';
import { WizardModal } from './WizardModal';

export class CallRoutingRule {
  routingId?: number | null = null;
  businessId?: string;
  name?: string;
  rule?: any;
  destination?: string;
  numbers?: any;
  isActive?: 0 | 1;
  order?: number | null = null;
  destinationType?: string;
  destinationId?: any;
  constructor(init?: Partial<CallRoutingRule>) {
    Object.assign(this, init);
  }
}

const useStyles = makeStyles((theme: Theme) => ({
  tab: {
    maxWidth: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
    },
    '&[tabindex="0"]': {
      backgroundColor: theme.palette.divider,
    },
  },
  tabIcon: {
    marginRight: 15,
  },
  tabs: {},
  reset: {
    margin: 0,
    padding: 0,
    alignContent: 'center',
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  warningIcon: {
    color: 'orange',
  },
}));

export interface IRoutingSelectorMap {
  value: string;
  viewValue: string;
}

export interface IRoutingSelectorRules {
  type: string;
  inputOptionType: string;
}
//TODO: add unavailable data to elastic search
const rules: IRoutingSelectorRules[] = [
  {
    type: 'Deal Value',
    inputOptionType: 'number',
  },
  {
    type: 'Deal Status',
    inputOptionType: 'text',
  },
  {
    type: 'No Contact Data',
    inputOptionType: 'boolean',
  },
  {
    type: 'Last Connection Date',
    inputOptionType: 'dateAgo',
  },
  {
    type: 'Last Call Date',
    inputOptionType: 'dateAgo',
  },
  {
    type: 'Last SMS Date',
    inputOptionType: 'dateAgo',
  },
  {
    type: 'Has Owner',
    inputOptionType: 'boolean',
  },
  {
    type: 'Deal Has Owner',
    inputOptionType: 'boolean',
  },
  {
    type: 'First Time Caller',
    inputOptionType: 'boolean',
  },
];

const operatorMap: IRoutingSelectorMap[] = [
  {
    value: '>=',
    viewValue: 'Greater than or equal to',
  },
  {
    value: '<=',
    viewValue: 'Less than or equal to',
  },
  {
    value: '==',
    viewValue: 'Equals',
  },
];

const InboundCallRoutingModal = (props: {
  show: boolean;
  handleClose: () => void;
  setRefetchToggle: any;
  id: any;
  groups: any;
  agents: any;
  ivrs: any;
  queues: any;
  conferences: any;
}) => {
  const location: any = useLocation();
  const [tabIndex, setTabIndex] = useState(0);
  const handleChange = (formik: FormikProps<any>) => async (
    event: any,
    newIndex: number
  ) => {
    setTabIndex(+newIndex);
  };
  const [userLoadable, setUserAtom] = useRecoilStateLoadable(user);
  const userContents = userLoadable.contents as IUserAtom;
  const mutation = useMutation(getApiMutation(userContents, setUserAtom));
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const classes = useStyles();
  const [filenames, setFilenames] = useState({
    greeting: '',
  }); // fields cannot be null/undefined
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, any>>({});
  const [filepaths, setFilepaths] = useState({
    greeting: '',
  });
  useEffect(() => {
    location?.state?.index >= 0 && setTabIndex(location?.state?.index);
  }, [location]);

  const [
    _,
    callRoutingRuleForEditLoadable,
    setRefetchToggleForEditRule,
  ] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_ROUTING_RULE,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_ROUTING_RULE,
        apikey: userContents?.apikey,
        businessId: userContents?.businessid,
        businessid: userContents?.businessid,
        routingId: props?.id,
      }),
      waitForPayload: !props?.id,
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );
  const callRoutingRuleForEdit =
    callRoutingRuleForEditLoadable?.contents?.response?.data?.result?.[0];
  React.useEffect(() => {
    setRefetchToggleForEditRule({});
  }, []);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        ruleActions: Yup.array().of(
          Yup.object().shape({
            type: Yup.string().required('required'),
            operand: Yup.string().required('required'),
            value: Yup.string().when('type', {
              is: (name) =>
                name !== 'Last Connection Date' &&
                name !== 'Last Call Date' &&
                name !== 'Last SMS Date' &&
                name !== 'Deal Value',
              then: Yup.string().required('required'),
            }),
            dealValue: Yup.number().when('type', {
              is: (name) => name === 'Deal Value',
              then: Yup.number().min(0, 'Minimum 0').required('required'),
            }),
            days: Yup.number().min(0, 'Minimum 0').max(90, 'Max 90'),
            hours: Yup.number().min(0, 'Minimum 0').max(23, 'Max 23'),
            minutes: Yup.number().min(0, 'Minimum 0').max(59, 'Max 59'),
          })
        ),
        defaultActionPayload: Yup.object().shape({
          type: Yup.string().required('required'),
          group: Yup.string().when('type', {
            is: (type) => type === 'group',
            then: Yup.string().required('Select a Group'),
          }),
          ivr: Yup.string().when('type', {
            is: (type) => type === 'ivr',
            then: Yup.string().required('Select a ivr'),
          }),
          queue: Yup.string().when('type', {
            is: (type) => type === 'queue',
            then: Yup.string().required('Select a queue'),
          }),
          user: Yup.string().when('type', {
            is: (type) => type === 'user',
            then: Yup.string().required('Select a user'),
          }),
        }),
        numberActions: Yup.array().of(
          Yup.object().shape({
            numberFor: Yup.string()
              .required('required')
              .oneOf(
                [
                  'Agent',
                  'Conference',
                  'Group',
                  'IVR',
                  'Local Presence',
                  'Connection Boost Pool',
                  'Queue',
                  'Unallocated',
                  'Number',
                ],
                'Must select at least one number type'
              ),
            agent: Yup.string().when(['numberFor', 'selectAll'], {
              is: (numberFor, selectAll) =>
                numberFor === 'Agent' && selectAll === false,
              then: Yup.string().required('Select at least one Agent'),
            }),
            conference: Yup.string().when(['numberFor', 'selectAll'], {
              is: (numberFor, selectAll) =>
                numberFor === 'Conference' && selectAll === false,
              then: Yup.string().required('Select at least one Conference'),
            }),
            group: Yup.string().when(['numberFor', 'selectAll'], {
              is: (numberFor, selectAll) =>
                numberFor === 'Group' && selectAll === false,
              then: Yup.string().required('Select at least one Group'),
            }),
            ivr: Yup.string().when(['numberFor', 'selectAll'], {
              is: (numberFor, selectAll) =>
                numberFor === 'IVR' && selectAll === false,
              then: Yup.string().required('Select at least one IVR'),
            }),
            localPresence: Yup.string().when(['numberFor', 'selectAll'], {
              is: (numberFor, selectAll) =>
                numberFor === 'Local Presence' && selectAll === false,
              then: Yup.string().required(
                'Select at least one Local Presence Number'
              ),
            }),
            queue: Yup.string().when(['numberFor', 'selectAll'], {
              is: (numberFor, selectAll) =>
                numberFor === 'Queue' && selectAll === false,
              then: Yup.string().required('Select at least one Queue'),
            }),
            unallocated: Yup.string().when(['numberFor', 'selectAll'], {
              is: (numberFor, selectAll) =>
                numberFor === 'Unallocated' && selectAll === false,
              then: Yup.string().required(
                'Select at least one Unallocated Number'
              ),
            }),
            number: Yup.string().when(['numberFor', 'selectAll'], {
              is: (numberFor, selectAll) =>
                numberFor === 'Number' && selectAll === false,
              then: Yup.string().required('Select at least one Number'),
            }),
          })
        ),
        destinationMessage:
          filenames.greeting !== ''
            ? Yup.object().shape({
                name: Yup.string()
                  .ensure()
                  .when(['showMessage', 'messageType'], {
                    is: (showMessage, messageType) =>
                      showMessage === true && messageType !== 'tts',
                    then: Yup.string()
                      .required('Required')
                      .checkFileSize(
                        20_000_000,
                        'File must be at or below 20mb'
                      ),
                  }),
              })
            : Yup.object().shape({
                message: Yup.string().when(['showMessage', 'messageType'], {
                  is: (showMessage, messageType) =>
                    showMessage === true && messageType === 'tts',
                  then: Yup.string().required('Must have a value'),
                }),
              }),
      }),
    [filenames]
  );

  const tabNames = [
    {
      title: 'Numbers',
      icon: AddIcCallIcon,
    },
    {
      title: 'Conditions',
      icon: FilterListIcon,
    },
    {
      title: 'Destination',
      icon: PhoneForwardedIcon,
    },
  ];
  const currentTabTitle = useMemo(() => {
    return tabNames[tabIndex]?.title ?? '';
  }, [tabIndex, tabNames]);
  const tabToFields = [
    ['numberActions'],
    ['ruleActions'],
    ['defaultActionPayload', 'destinationMessage'],
  ];

  const checkDate = (item) => {
    return (
      item.type !== 'Last Connection Date' &&
      item.type !== 'Last Call Date' &&
      item.type !== 'Last SMS Date'
    );
  };

  const addNewRuleInitialValues = {
    routingId: null,
    ruleActions: [
      {
        value: '',
        rule: {
          type: '',
          inputOptionType: '',
        },
        type: '',
        operand: '',
        dealValue: 0,
        days: 0,
        hours: 0,
        minutes: 0,
      } as any,
    ],
    defaultActionPayload: {
      type: 'voicemail',
      ivr: '',
      group: '',
      user: '',
      queue: '',
    },
    numberActions: [
      {
        numberFor: 'None',
        selectAll: false,
        agent: [],
        conference: [],
        group: [],
        ivr: [],
        queue: [],
        unallocated: [],
        localPresence: [],
        number: [],
      } as any,
    ],
    destinationMessage: {
      showMessage: false,
      messageType: 'tts',
      message: 'call will be re-routed',
      file: '',
      name: '',
    },
  };
  const getNumObj = (props) => {
    const obj = {
      numberFor: 'None',
      selectAll: false,
      agent: [],
      conference: [],
      group: [],
      ivr: [],
      queue: [],
      unallocated: [],
      localPresence: [],
      number: [],
    } as any;
    const updatedObj = { ...obj, ...props };
    return updatedObj;
  };

  const getNumberActions = (numObj: any) => {
    const actions: any = [];
    for (const key in numObj) {
      if (numObj[key].length) {
        if (key !== 'All') {
          const number: any = [];
          const unallocated: any = [];
          const localPresence: any = [];
          if (key === 'Number' && numObj[key].length) {
            numObj[key].forEach((el: any) => {
              if (
                businessNumbersCached?.current?.lookupNumbers?.[el]?.type ===
                'unallocated'
              ) {
                unallocated.push(el);
              } else if (
                businessNumbersCached?.current?.lookupNumbers?.[el]
                  ?.localpresence === 1
              ) {
                localPresence.push(el);
              } else {
                number.push(el);
              }
            });
            if (unallocated.length) {
              actions.push(
                getNumObj({
                  numberFor: 'Unallocated',
                  unallocated: unallocated,
                })
              );
            }
            if (localPresence.length) {
              actions.push(
                getNumObj({
                  numberFor: 'Local Presence',
                  localPresence: localPresence,
                })
              );
            }
            if (number.length) {
              actions.push(getNumObj({ numberFor: 'Number', number: number }));
            }
          } else {
            actions.push({
              numberFor: key,
              selectAll: false,
              agent: key === 'Agent' ? numObj[key] : [],
              conference: key === 'Conference' ? numObj[key] : [],
              group: key === 'Group' ? numObj[key] : [],
              ivr: key === 'IVR' ? numObj[key] : [],
              queue: key === 'Queue' ? numObj[key] : [],
              unallocated: [],
              localPresence: [],
              number: key === 'Number' ? numObj[key] : [],
            } as any);
          }
        } else {
          numObj[key].forEach((el) => {
            const numFor =
              el === 'localPresence'
                ? 'Local Presence'
                : el === 'localPresenceLite'
                ? 'Connection Boost Pool'
                : el === 'ivr'
                ? 'IVR'
                : el.charAt(0).toUpperCase() + el.slice(1);
            actions.push(getNumObj({ numberFor: numFor, selectAll: true }));
          });
        }
      }
    }
    return actions.length
      ? actions
      : [
          {
            numberFor: 'None',
            selectAll: false,
            agent: [],
            conference: [],
            group: [],
            ivr: [],
            queue: [],
            unallocated: [],
            localPresence: [],
            number: [],
          } as any,
        ];
  };

  const onCloseRoutingModal = () => {
    setRefetchToggleForEditRule({});
    props.handleClose();
  };
  const [businessNumbersCached, businessNumbersLoadable] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_NUMBERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_NUMBERS,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.reduce(
        (acc, item) => ({
          ...acc,
          allNumbers: acc.allNumbers.concat(item.DID),
          lookupNumbers: {
            ...acc.lookupNumbers,
            [item.DID]: item,
          },
          lookupAgents:
            item.type === 'Agent'
              ? {
                  ...acc.lookupAgents,
                  [item.userid]: item,
                }
              : { ...acc.lookupAgents },
        }),
        {
          allNumbers: [],
          lookupNumbers: {},
          lookupAgents: {},
        }
      ) ?? cached.current,
    {
      allNumbers: [],
      lookupNumbers: {},
      lookupAgents: {},
    },
    []
  );

  const editRuleInitialValues = useMemo<any>(() => {
    return callRoutingRuleForEditLoadable?.state === 'hasValue' &&
      callRoutingRuleForEditLoadable?.contents?.response?.data?.success &&
      businessNumbersLoadable?.state === 'hasValue'
      ? {
          routingId: callRoutingRuleForEdit?.routingId,
          ruleActions: JSON.parse(callRoutingRuleForEdit?.rule)?.map((r) => ({
            value: r.type !== 'Deal Value' ? r.value : '',
            rule: {
              type: r.type,
              inputOptionType: rules.filter((ru) => ru.type === r.type)[0]
                .inputOptionType,
            },
            dealValue: r.type === 'Deal Value' ? r.value : 0,
            type: r.type,
            days: !checkDate(r) ? Math.floor(Number(r.value) / (60 * 24)) : 0,
            hours: !checkDate(r)
              ? Math.floor((Number(r.value) % (60 * 24)) / 60)
              : 0,
            minutes: !checkDate(r) ? Math.floor(Number(r.value) % 60) : 0,
            operand: r.operand,
          })) as any,
          defaultActionPayload: {
            type: callRoutingRuleForEdit?.destinationType ?? '',
            ivr:
              callRoutingRuleForEdit?.destinationType === 'ivr'
                ? callRoutingRuleForEdit?.destinationId
                : '',
            group:
              callRoutingRuleForEdit?.destinationType === 'group'
                ? callRoutingRuleForEdit?.destinationId
                : '',
            user:
              callRoutingRuleForEdit?.destinationType === 'user'
                ? callRoutingRuleForEdit?.destinationId
                : '',
            queue:
              callRoutingRuleForEdit?.destinationType === 'queue'
                ? callRoutingRuleForEdit?.destinationId
                : '',
          },
          numberActions: getNumberActions(
            JSON.parse(callRoutingRuleForEdit?.numbers)
          ),
          destinationMessage: {
            showMessage: !!callRoutingRuleForEdit?.greeting,
            messageType: callRoutingRuleForEdit?.greeting // if so does the greeting contain an mp3 url?
              ? callRoutingRuleForEdit?.greeting?.endsWith('.mp3')
                ? // if so set the greeting type to mp3
                  'mp3'
                : // otherwise we assume it is a tts value
                  'tts'
              : 'tts',
            message: callRoutingRuleForEdit?.greeting?.endsWith('.mp3')
              ? ''
              : callRoutingRuleForEdit?.greeting ?? 'call will be re-routed',
            file: '',
            name: isUrl(callRoutingRuleForEdit?.greeting)
              ? callRoutingRuleForEdit?.greeting?.split('/').slice(-1).pop() ??
                ''
              : '',
          },
        }
      : addNewRuleInitialValues;
  }, [callRoutingRuleForEdit, businessNumbersLoadable, businessNumbersCached]);

  useEffect(() => {
    setFilenames({
      greeting: isUrl(callRoutingRuleForEdit?.greeting)
        ? callRoutingRuleForEdit?.greeting.split('/').slice(-1).pop() ?? ''
        : '', // filename from uri/path
    });
    setFilepaths({
      greeting: isUrl(callRoutingRuleForEdit?.greeting)
        ? callRoutingRuleForEdit?.greeting ?? ''
        : '',
    });
  }, [callRoutingRuleForEdit]);
  function trackUpdates(values, editRuleInitialValues, currentTabTitle) {
    trackModalUpdates(
      values,
      editRuleInitialValues,
      'Inbound Rule Settings Updated',
      currentTabTitle
    );
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={
        props?.id ? editRuleInitialValues : addNewRuleInitialValues
      }
      validationSchema={validationSchema}
      onSubmit={async (values: any, actions) => {
        trackUpdates(values, editRuleInitialValues, currentTabTitle);
        const destinationType = values?.defaultActionPayload?.type;
        if (destinationType === 'owner' || destinationType === 'dealOwner') {
          let invalidDestination = true;
          values?.ruleActions?.forEach((rule, i) => {
            if (
              (rule?.type === 'Has Owner' && rule?.value === 'true') ||
              (rule?.type === 'Deal Has Owner' && rule?.value === 'true')
            ) {
              invalidDestination = false;
            }
          });
          if (invalidDestination) {
            enqueueSnackbarWithStyles({
              message: `Invalid destination`,
              alertProps: { severity: 'error' },
            });
            setTabIndex(2);
            return;
          }
        }
        const payload = new CallRoutingRule({
          routingId: values?.routingId,
          businessId: String(userContents.businessid),
          name: values?.ruleActions?.reduce((prev, curr, idx) => {
            const isNotDate = checkDate(curr);
            const p = idx !== 0 ? prev + ', ' : prev;
            const operatorName =
              curr.operand === '>=' ||
              curr.operand === '<=' ||
              curr.operand === '=='
                ? operatorMap.find((el) => el.value === curr.operand)?.viewValue
                : curr.operand;
            return isNotDate
              ? p +
                  `${curr.type} ${operatorName} ${
                    curr?.rule?.type === 'Deal Value'
                      ? curr.dealValue
                      : curr.value
                  }`
              : p +
                  `${curr.type} ${operatorName}` +
                  ' ' +
                  (curr.days !== 0 ? curr.days + ' days' : '') +
                  ' ' +
                  (curr.hours !== 0 ? curr.hours + ' hours' : '') +
                  ' ' +
                  (curr.minutes !== 0 ? curr.minutes + ' minutes' : '');
          }, ''),
          rule: values.ruleActions.map((el, i) => {
            return {
              type: el.type,
              operand: el.operand,
              value:
                el.type === 'Deal Value'
                  ? el.dealValue
                  : checkDate(el)
                  ? el.value
                  : Number(el.days) * 1440 +
                    Number(el.hours) * 60 +
                    Number(el.minutes),
            };
          }),
          numbers: values?.numberActions?.map((el, i) => {
            const type = el.numberFor.toLowerCase();
            return {
              type: el.numberFor,
              values:
                el.selectAll || type === 'connection boost pool'
                  ? ['selectAll']
                  : el.numberFor === 'Local Presence'
                  ? el.localPresence
                  : el[type],
            };
          }),
          isActive: 1,
          destinationType: values.defaultActionPayload?.type,
          destinationId: values.defaultActionPayload[destinationType] ?? null,
        });
        const rule = {
          rule: payload.rule,
          numbers: payload.numbers,
          routingId: props?.id ?? null,
        };
        try {
          let data = {
            call: methodNameEnum.UPSERT_ROUTING_RULE,
            apikey: userContents?.apikey,
            businessid: userContents?.businessid,
            userid: userContents?.userid,
            name: payload.name,
            businessId: payload.businessId,
            destinationType: payload.destinationType,
            destinationId: payload.destinationId,
            isActive: payload.isActive,
            rule: JSON.stringify(rule),
          };
          if (
            values?.destinationMessage?.showMessage &&
            values?.destinationMessage?.messageType === 'tts' &&
            (destinationType === 'owner' || destinationType === 'dealOwner')
          ) {
            data = {
              ...data,
              greeting: values?.destinationMessage?.message ?? '',
            } as any;
          }
          if (values?.destinationMessage?.showMessage === false) {
            (data as any).greeting = '';
          }
          const response = await mutation.mutateAsync({
            endpoint: endpointEnum.UPSERT_ROUTING_RULE,
            payload: data,
          });
          if (response.data.success === false) {
            enqueueSnackbarWithStyles({
              message:
                response?.data?.error ??
                `Error ${props?.id ? 'updating' : 'adding'} routing condition`,
              alertProps: { severity: 'error' },
            });
          } else {
            const newRoutingId = !props?.id
              ? response?.data?.result?.[1]?.[0]?.routingId
              : props?.id;
            if (
              destinationType === 'owner' ||
              destinationType === 'dealOwner'
            ) {
              if (values?.destinationMessage?.showMessage) {
                if (values?.destinationMessage?.messageType === 'record') {
                  const data = new FormData();
                  data.append('webm', uploadedFiles.greeting[0]);
                  data.append('businessid', String(userContents.businessid));
                  data.append('apikey', userContents.apikey);
                  data.append('userid', String(userContents.userid));
                  data.append('call', 'HandleAudioRecording');
                  data.append('id', String(newRoutingId));
                  data.append('iid', 0);
                  data.append('name', values.destinationMessage.name);
                  data.append('type', 'routing-greeting');
                  const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
                  await Axios.post(url, data, { withCredentials: true });
                }
                if (
                  values?.destinationMessage?.messageType === 'mp3' &&
                  uploadedFiles?.greeting
                ) {
                  const data = new FormData();
                  data.append('call', 'submitRoutingMp3File');
                  data.append('businessid', String(userContents.businessid));
                  data.append('apikey', userContents.apikey);
                  data.append('userid', String(userContents.userid));
                  data.append('name', values.destinationMessage.name);
                  data.append('mp3', uploadedFiles.greeting[0]);
                  data.append('id', newRoutingId);
                  const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
                  await Axios.post(url, data, { withCredentials: true });
                }
              }
            }
            enqueueSnackbarWithStyles({
              message: `Success ${
                props?.id ? 'updating' : 'adding'
              } routing condition`,
              alertProps: { severity: 'success' },
            });
          }
        } catch (err) {
          enqueueSnackbarWithStyles({
            message: `Error ${
              props?.id ? 'updating' : 'adding'
            } routing condition`,
            alertProps: { severity: 'error' },
          });
        }
        props.setRefetchToggle({});
        actions.resetForm({});
        onCloseRoutingModal();
      }}
    >
      {(formik) => {
        return (
          <TabContext value={String(tabIndex)}>
            <WizardModal
              title={tabNames[tabIndex].title}
              navigation={
                <>
                  <Tab
                    label={
                      props.id ? `Edit Inbound Rule` : `Add new Inbound Rule`
                    }
                    disabled={true}
                    className={clsx(classes.tab)}
                  />
                  <TabList
                    onChange={handleChange(formik)}
                    aria-label="Add new Inbound Condition"
                    orientation="vertical"
                    variant="standard"
                    className={clsx(classes.tabs, {})}
                  >
                    {tabNames.map((tabItem, index) => (
                      <Tab
                        // name={tabItem.title}
                        key={index}
                        className={classes.tab}
                        label={
                          <TabLabel
                            index={index}
                            formik={formik}
                            tabToFields={tabToFields}
                            tabItem={tabItem}
                          />
                        }
                        value={String(index)}
                        data-pup={`routing-rule-${tabItem.title}-tab`}
                        sx={{ alignItems: 'start' }}
                      />
                    ))}
                  </TabList>
                </>
              }
              dialogActions={
                <>
                  <Button
                    className={classes.button}
                    disabled={formik.isSubmitting}
                    variant="contained"
                    color="inherit"
                    disableElevation={true}
                    onClick={onCloseRoutingModal}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    className={clsx(classes.button)}
                    variant="contained"
                    disableElevation={true}
                    type="submit"
                    disabled={
                      (Object.keys(formik.touched).length === 0 &&
                        !formik.dirty) ||
                      formik.isSubmitting ||
                      !formik.dirty
                    }
                    onClick={(event: any) => {
                      formik.handleSubmit(event);
                      if (
                        !formik.isValid &&
                        !isErrorInTab(tabIndex, formik.errors, tabToFields)
                      ) {
                        const errorTabIndex = getErrorTabIndex(
                          formik.errors,
                          tabToFields
                        );
                        setTimeout(() => setTabIndex(errorTabIndex ?? 0));
                      }
                    }}
                    data-pup={'routing-rule-save-button'}
                  >
                    Save
                  </Button>
                </>
              }
              size="lg"
              show={props.show}
              onHide={onCloseRoutingModal}
              onSubmit={() => {
                trackSaveInboundRouting();
                formik.handleSubmit();
              }}
              isLoading={
                formik.isSubmitting ||
                businessNumbersLoadable.state === 'loading'
              }
              isSubmitting={formik.isSubmitting}
            >
              <TabPanel value="0">
                <NumberTabActions
                  groupDetails={props.groups}
                  agentDetails={props.agents}
                  ivrDetails={props.ivrs}
                  queueDetails={props.queues}
                  conferenceDetails={props.conferences}
                  businessNumbersCached={businessNumbersCached}
                  businessNumbersLoadable={businessNumbersLoadable}
                  id={props?.id ?? null}
                  formik={formik}
                />
              </TabPanel>
              <TabPanel value="1">
                <RuleTabActions
                  formik={formik}
                  rules={rules}
                  id={props?.id ?? null}
                />
              </TabPanel>
              <TabPanel value="2">
                <DestinationTabActions
                  formik={formik}
                  setFilenames={setFilenames}
                  filenames={filenames}
                  setUploadedFiles={setUploadedFiles}
                  setFilepaths={setFilepaths}
                  filepaths={filepaths}
                  uploadedFiles={uploadedFiles}
                />
              </TabPanel>
            </WizardModal>
          </TabContext>
        );
      }}
    </Formik>
  );
};

export default InboundCallRoutingModal;
